import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Activity, Target, Server, DollarSign, Shield, TrendingUp, Users, FileText, Gauge, LogOut, ChevronDown, ChevronRight, Menu, X, ShieldCheck, Package, Megaphone, Search, ShoppingBag, BarChart2, Globe, Crosshair, Inbox } from 'lucide-react';
import { COLORS } from './governanceTheme';
import { AZURE } from '../../services/api';
import R from '../../navigation/opsRoutes';
import { FEATURES } from '../../config/features';
import SEOHead from '../seo/SEOHead';
import OpsNotificationsBell from './OpsNotificationsBell';
import { GovernanceLangProvider, useGovernanceLang } from '../../contexts/GovernanceLangContext';

// badge: 'live' | 'wip' | 'mock' — mostrado ao lado do label no sidebar
const buildNav = (lang, counts = {}) => {
  const e = lang === 'en';
  return [
  { id: 'overview',    to: R.overview,    label: e ? 'Overview'          : 'Overview',          icon: Activity,   exact: true },
  { id: 'usuarios',   to: R.usuarios,    label: e ? 'Users & Access'    : 'Usuários & Acessos', icon: Users,      matchPrefix: R.usuarios },
  { id: 'autorizacoes', to: R.autorizacoes, label: e ? 'Authorizations' : 'Autorizações',       icon: Shield,     matchPrefix: R.autorizacoes },
  { id: 'rh',         to: R.rh,          label: e ? 'People & HR'       : 'RH & Pessoas',       icon: Users,      matchPrefix: R.rh,          badge: 'wip' },
  {
    id: 'comercial', label: e ? 'Commercial' : 'Comercial', icon: Package,
    matchPrefixes: ['/governance/comercial', R.clientes, R.marketplace, R.usageDashboard],
    children: [
      { to: R.clientes,        label: e ? 'Clients'       : 'Clientes',            icon: Users,      matchPrefix: R.clientes },
      { to: R.comercialPlanos, label: e ? 'Plans'         : 'Planos',              icon: Package,    matchPrefix: R.comercialPlanos },
      { to: R.comercialLeads,  label: e ? 'Smart Leads'  : 'Leads Inteligentes',  icon: Crosshair,  matchPrefix: R.comercialLeads },
      { to: R.comercialAccessRequests, label: e ? 'Access Requests' : 'Solicitações de Acesso', icon: Inbox, matchPrefix: R.comercialAccessRequests, countBadge: counts.accessRequestsNew },
      { to: R.marketplace,     label: 'Marketplace',                               icon: ShoppingBag, matchPrefix: R.marketplace, badge: 'wip' },
      { to: R.usageDashboard,  label: e ? 'Usage & Quotas' : 'Usage & Quotas',    icon: BarChart2,  matchPrefix: R.usageDashboard },
    ],
  },
  { id: 'fin', label: e ? 'Finance' : 'Financeiro', icon: DollarSign, to: R.fin, matchPrefix: R.fin },
  {
    id: 'ti', label: e ? 'IT & Infra' : 'TI & Infra', icon: Server, matchPrefix: '/governance/ti',
    children: [
      { to: R.tiControlTower, label: 'Control Tower',                           icon: Gauge },
      { to: R.tiSLOs,         label: e ? 'SLOs & Incidents' : 'SLOs & Incidentes', icon: TrendingUp },
      { to: R.tiAgentes,      label: e ? 'AI Agents'        : 'Agentes AI',        icon: Shield },
      { to: R.tiMonitoring,   label: e ? 'Monitoring'       : 'Monitoramento',     icon: Activity },
    ],
  },
  {
    id: 'exec', label: e ? 'Projects' : 'Projetos', icon: Target, matchPrefixes: ['/governance/exec', R.competeMinas],
    children: [
      { to: R.execPlanner, label: 'Planner',              icon: Target },
      // Gate P1: Builder oculto por padrão (GOV_BUILDER_ENABLED)
      ...(FEATURES.GOV_BUILDER_ENABLED
        ? [{ to: R.execBuilder, label: 'Builder / Brain', icon: Activity }]
        : []),
      { to: R.competeMinas, label: 'Compete Minas', icon: FileText, matchPrefix: R.competeMinas, badge: 'live' },
    ],
  },
  { id: 'investors', to: R.investidores, label: e ? 'Investors'  : 'Investidores', icon: TrendingUp, matchPrefix: R.investidores, badge: 'wip' },
  { id: 'purview',   to: R.purview,     label: 'Purview',                          icon: ShieldCheck, matchPrefix: R.purview,    badge: 'wip' },
  {
    id: 'marketing', label: 'Marketing', icon: Megaphone, badge: 'wip',
    matchPrefixes: ['/governance/marketing', R.editorialEngine],
    children: [
      { to: R.marketingHub,     label: 'Hub',                                       icon: Megaphone, matchPrefix: R.marketingHub },
      { to: R.marketingSeo,     label: 'SEO',                                       icon: Search,    matchPrefix: R.marketingSeo },
      { to: R.editorialEngine,  label: e ? 'Editorial Engine' : 'Editorial Engine', icon: FileText },
    ],
  },
  ];
};

// ─── Sidebar Nav Item ──────────────────────────────────────────────────────
const NavItem = ({ item, location, depth = 0 }) => {
  const hasChildren = item.children && item.children.length > 0;
  const isActive = item.exact
    ? (location.pathname === item.to || location.pathname === item.to + '/' || location.pathname === '/')
    : item.matchPrefixes
      ? item.matchPrefixes.some(p => location.pathname.startsWith(p))
      : item.matchPrefix
        ? location.pathname.startsWith(item.matchPrefix)
        : item.to && location.pathname.startsWith(item.to);

  const [open, setOpen] = useState(isActive);

  if (hasChildren) {
    return (
      <div>
        <button
          onClick={() => setOpen(!open)}
          className={`w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150
            ${isActive ? 'bg-gray-800 text-white' : 'text-gray-400 hover:text-white hover:bg-gray-800/50'}`}
        >
          <item.icon className="w-4 h-4 flex-shrink-0" />
          <span className="flex-1 text-left">{item.label}</span>
          {item.badge && (
            <span className="px-1 py-0.5 text-[9px] font-bold rounded mr-1"
              style={{ backgroundColor: item.badge === 'wip' ? '#92400e' : COLORS.gold, color: '#fff', opacity: .85 }}>
              {item.badge.toUpperCase()}
            </span>
          )}
          {open ? <ChevronDown className="w-3.5 h-3.5" /> : <ChevronRight className="w-3.5 h-3.5" />}
        </button>
        {open && (
          <div className="ml-4 mt-0.5 space-y-0.5 border-l border-gray-700 pl-2">
            {item.children.map(child => (
              <NavItem key={child.to} item={child} location={location} depth={depth + 1} />
            ))}
          </div>
        )}
      </div>
    );
  }

  return (
    <Link
      to={item.to}
      className={`flex items-center gap-2.5 px-3 py-2 text-sm font-medium rounded-lg transition-all duration-150
        ${isActive
          ? depth > 0
            ? 'bg-gray-700/60 text-white'
            : 'bg-gray-800 text-white'
          : 'text-gray-400 hover:text-white hover:bg-gray-800/50'
        }`}
    >
      <item.icon className="w-4 h-4 flex-shrink-0" />
      <span>{item.label}</span>
      {item.countBadge > 0 && (
        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded-full"
          style={{ backgroundColor: '#dc2626', color: '#fff', minWidth: 18, textAlign: 'center' }}>
          {item.countBadge}
        </span>
      )}
      {item.badge && (
        <span className="ml-auto px-1.5 py-0.5 text-[10px] font-bold rounded"
          style={{ backgroundColor: COLORS.gold, color: '#fff' }}>
          {item.badge}
        </span>
      )}
    </Link>
  );
};

// ─── Lang Toggle ──────────────────────────────────────────────────────────
const LangToggle = () => {
  const { lang, setLang } = useGovernanceLang();
  return (
    <div className="flex items-center gap-1 px-3 py-1">
      <span className="text-[10px] text-gray-500 uppercase tracking-widest mr-1">Lang</span>
      <div className="flex rounded-md overflow-hidden border border-gray-700">
        <button
          onClick={() => setLang('pt')}
          className="px-2.5 py-1 text-xs font-bold transition-colors"
          style={lang === 'pt'
            ? { backgroundColor: COLORS.gold, color: '#fff' }
            : { backgroundColor: 'transparent', color: '#9ca3af' }}
        >
          PT
        </button>
        <button
          onClick={() => setLang('en')}
          className="px-2.5 py-1 text-xs font-bold transition-colors"
          style={lang === 'en'
            ? { backgroundColor: COLORS.gold, color: '#fff' }
            : { backgroundColor: 'transparent', color: '#9ca3af' }}
        >
          EN
        </button>
      </div>
    </div>
  );
};

// ─── Main Layout Inner (must live inside GovernanceLangProvider) ───────────
const GovernanceLayoutInner = ({ children, opsMode = false, opsUser = null }) => {
  const location = useLocation();
  const { lang } = useGovernanceLang();
  const [accessRequestsNew, setAccessRequestsNew] = useState(0);

  useEffect(() => {
    let alive = true;
    AZURE.get('/governance/commercial/access-requests/summary')
      .then(({ data }) => { if (alive && data?.success) setAccessRequestsNew(data.data?.new || 0); })
      .catch(() => {}); // badge é opcional — nunca quebra o layout
    return () => { alive = false; };
  }, [location.pathname]);

  const navItems = buildNav(lang, { accessRequestsNew });
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = () => {
    if (opsMode) {
      window.location.href = '/.auth/logout?post_logout_redirect_uri=/';
    } else {
      localStorage.removeItem('arena_stats_user');
      window.location.href = '/login';
    }
  };

  const sidebarContent = (
    <>
      {/* Logo + Brand + Sino */}
      <div className="px-4 pt-5 pb-4 border-b border-gray-700/50 flex items-center justify-between">
        <Link to={R.overview} className="flex items-center gap-3">
          <img
            src="/vukz-logo.png"
            alt="Vukz"
            className="w-10 h-10 rounded-lg object-contain bg-white p-0.5"
            onError={(e) => { e.target.style.display = 'none'; }}
          />
          <div>
            <h1 className="text-lg font-extrabold text-white tracking-tight leading-tight">vukz.ai</h1>
            <p className="text-[11px] text-gray-400 font-medium">{lang === 'en' ? 'Operational Hub' : 'Central Operacional'}</p>
          </div>
        </Link>
        <OpsNotificationsBell />
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto px-3 py-4 space-y-1">
        {navItems.map(item => (
          <NavItem key={item.id} item={item} location={location} />
        ))}
      </nav>

      {/* Footer: lang toggle + user + logout */}
      <div className="px-3 py-4 border-t border-gray-700/50 space-y-2">
        <LangToggle />
        {opsUser && (
          <div className="px-3 py-1">
            <p className="text-xs text-gray-400 truncate">{opsUser.email}</p>
          </div>
        )}
        <button
          onClick={handleLogout}
          className="w-full flex items-center gap-2.5 px-3 py-2 text-sm font-medium text-gray-400 hover:text-red-400 hover:bg-gray-800/50 rounded-lg transition-all duration-150"
        >
          <LogOut className="w-4 h-4" />
          <span>{lang === 'en' ? 'Sign out' : 'Sair'}</span>
        </button>
      </div>
    </>
  );

  return (
    <div className="min-h-screen flex" style={{ backgroundColor: COLORS.bg }}>
      <SEOHead title="Governance" noindex />
      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:flex-col lg:w-60 lg:fixed lg:inset-y-0 bg-[#111111] z-40">
        {sidebarContent}
      </aside>

      {/* Mobile header bar */}
      <div className="lg:hidden fixed top-0 left-0 right-0 h-14 bg-[#111111] flex items-center px-4 z-40">
        <button onClick={() => setMobileOpen(true)} className="text-white">
          <Menu className="w-6 h-6" />
        </button>
        <div className="ml-3 flex items-center gap-2">
          <img src="/vukz-logo.png" alt="Vukz" className="w-7 h-7 rounded object-contain bg-white p-0.5"
            onError={(e) => { e.target.style.display = 'none'; }} />
          <span className="text-white font-bold text-sm">vukz.ai</span>
        </div>
        <div className="ml-auto">
          <OpsNotificationsBell />
        </div>
      </div>

      {/* Mobile sidebar overlay */}
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative flex flex-col w-64 bg-[#111111]">
            <button onClick={() => setMobileOpen(false)} className="absolute top-4 right-4 text-gray-400 hover:text-white">
              <X className="w-5 h-5" />
            </button>
            {sidebarContent}
          </div>
        </div>
      )}

      {/* Content */}
      <main className="flex-1 lg:ml-60">
        <div className="pt-14 lg:pt-0">
          <div className="max-w-7xl mx-auto px-4 lg:px-6 py-6">
            {children}
          </div>
        </div>
      </main>
    </div>
  );
};

// ─── Public export: GovernanceLangProvider is now global (App.js) ─────────
const GovernanceLayout = ({ children, opsMode = false, opsUser = null }) => (
  <GovernanceLayoutInner opsMode={opsMode} opsUser={opsUser}>
    {children}
  </GovernanceLayoutInner>
);

export default GovernanceLayout;
